import { useState } from 'react';
import { api } from '../../api';
import type { DetectedFacts, Episode, MediaItem } from '../../types';
import { ActionBar, StepHeader, primaryBtn } from './ui';

/**
 * Step 3: what the app detected, plus the title, season name and cover — all
 * editable. Only facts the files actually recorded are shown; nothing is guessed.
 */
export default function ReviewStep({
  episode,
  items,
  detected,
  onDone,
}: {
  episode: Episode;
  items: MediaItem[];
  detected: DetectedFacts;
  onDone: () => void;
}) {
  const [title, setTitle] = useState(episode.title);
  const [seasonTitle, setSeasonTitle] = useState(detected.season?.title ?? '');
  const [coverId, setCoverId] = useState(episode.coverMediaId ?? null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const withThumbs = items.filter((i) => i.thumbnailUrl);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const patch: { title?: string; coverMediaId?: string | null } = {};
      if (title.trim() !== episode.title) patch.title = title;
      if (coverId !== episode.coverMediaId) patch.coverMediaId = coverId;
      if (Object.keys(patch).length > 0) await api.updateEpisode(episode.id, patch);
      if (detected.season && seasonTitle.trim() && seasonTitle.trim() !== detected.season.title) {
        await api.renameSeason(detected.season.year, seasonTitle);
      }
      onDone();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const counts = [
    detected.photoCount > 0 && `${detected.photoCount} ${detected.photoCount === 1 ? 'photo' : 'photos'}`,
    detected.videoCount > 0 && `${detected.videoCount} ${detected.videoCount === 1 ? 'video' : 'videos'}`,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div>
      <StepHeader step={3} title="Here’s your episode" subtitle="This is what your photos told us. Change anything you like." />

      <dl className="grid grid-cols-1 gap-3 rounded-2xl bg-ink-900 p-5 text-sm ring-1 ring-white/5 sm:grid-cols-3">
        <Fact label="When">
          {detected.dateLabel ?? <span className="text-white/45">No date recorded in these files</span>}
          {detected.dateLabel && !detected.dateRecorded && (
            <span className="block text-xs text-white/40">
              from the files’ saved times; the photos themselves recorded no date
            </span>
          )}
          {detected.dateRecorded && detected.undatedCount > 0 && (
            <span className="block text-xs text-white/40">
              from {detected.datedCount} of {detected.datedCount + detected.undatedCount}; the rest recorded no date
            </span>
          )}
        </Fact>
        <Fact label="Where">
          {detected.places.length > 0 ? (
            detected.places.join(', ')
          ) : detected.place ? (
            detected.place
          ) : (
            <span className="text-white/45">No location recorded</span>
          )}
        </Fact>
        <Fact label="Memories">{counts}</Fact>
      </dl>

      <label className="mt-6 block">
        <span className="mb-1.5 block text-sm font-semibold">Episode title</span>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={120}
          className="focus-ring w-full rounded-lg bg-white/[0.07] px-4 py-3 text-base ring-1 ring-white/10"
        />
        {episode.generatedTitle && title.trim() !== episode.generatedTitle && (
          <button
            type="button"
            onClick={() => setTitle(episode.generatedTitle!)}
            className="focus-ring mt-1.5 rounded text-xs text-white/45 hover:text-white/80"
          >
            Use the suggestion: {episode.generatedTitle}
          </button>
        )}
      </label>

      <label className="mt-5 block">
        <span className="mb-1.5 block text-sm font-semibold">Season</span>
        {detected.season ? (
          <>
            <input
              value={seasonTitle}
              onChange={(e) => setSeasonTitle(e.target.value)}
              maxLength={120}
              className="focus-ring w-full rounded-lg bg-white/[0.07] px-4 py-3 text-base ring-1 ring-white/10"
            />
            <span className="mt-1.5 block text-xs text-white/40">
              Every episode from {detected.season.year} is in this season. Renaming it renames it everywhere.
            </span>
          </>
        ) : (
          <span className="block text-sm text-white/50">
            These files recorded no date, so the episode goes in “Undated memories” rather than a year.
          </span>
        )}
      </label>

      {withThumbs.length > 0 && (
        <fieldset className="mt-6">
          <legend className="mb-2 text-sm font-semibold">Cover</legend>
          <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
            {withThumbs.map((m) => (
              <button
                key={m.id}
                type="button"
                aria-pressed={coverId === m.id}
                aria-label={`Use ${m.originalFilename} as the cover`}
                onClick={() => setCoverId(m.id)}
                className={`focus-ring relative aspect-video overflow-hidden rounded-md ring-2 transition ${
                  coverId === m.id ? 'ring-accent' : 'ring-transparent opacity-70 hover:opacity-100'
                }`}
              >
                <img src={m.thumbnailUrl!} alt="" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        </fieldset>
      )}

      {error && (
        <p className="mt-4 text-sm text-red-200" role="alert">
          {error}
        </p>
      )}

      <ActionBar>
        <button onClick={save} disabled={saving || !title.trim()} className={primaryBtn}>
          {saving ? 'Saving…' : 'Looks good'}
        </button>
      </ActionBar>
    </div>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-semibold tracking-wider text-white/40 uppercase">{label}</dt>
      <dd className="mt-1 font-medium">{children}</dd>
    </div>
  );
}
